import React, { useState, useMemo } from 'react';
import { View, Text, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';

const centsToDollars = (cents) => (cents / 100).toFixed(2);

export default function RecurringGiftCard({ gift, funds = [], onUpdate, testID = 'recurring-gift-card' }) {
    const [expanded, setExpanded] = useState(false);

    const amount = useMemo(() => centsToDollars(gift.amount || 0), [gift.amount]);
    const nextRun = gift.nextRunAt ? new Date(gift.nextRunAt).toLocaleDateString() : '—';
    const fundNames = useMemo(() => {
        return (gift.fundSplits || []).map((split) => {
            const fund = funds.find((f) => f.id === split.fundID);
            return fund?.name || 'Unknown fund';
        }).join(', ');
    }, [gift.fundSplits, funds]);

    const isActive = gift.status === 'ACTIVE';
    const isPaused = gift.status === 'PAUSED';
    const isCancelled = gift.status === 'CANCELLED';

    return (
        <Pressable testID={testID} onPress={() => setExpanded(!expanded)} style={styles.card}>
            <View style={styles.header}>
                <View style={styles.headerLeft}>
                    <Unicon name="repeat" size={20} colorVariant="giving" />
                    <View>
                        <Text style={styles.amount}>${amount}</Text>
                        <Text style={styles.meta}>{gift.frequency?.toLowerCase()} • Next {nextRun}</Text>
                    </View>
                </View>
                <View style={[styles.statusBadge, isActive && styles.activeBadge, isPaused && styles.pausedBadge, isCancelled && styles.cancelledBadge]}>
                    <Text style={[styles.statusText, isActive && styles.activeText, isPaused && styles.pausedText, isCancelled && styles.cancelledText]}>
                        {gift.status}
                    </Text>
                </View>
            </View>
            {expanded && (
                <View style={styles.details} testID={`${testID}-details`}>
                    <Text style={styles.label}>Funds</Text>
                    <Text style={styles.body}>{fundNames || '—'}</Text>
                    {!!gift.note && (
                        <>
                            <Text style={styles.label}>Note</Text>
                            <Text style={styles.body}>{gift.note}</Text>
                        </>
                    )}
                    <Text style={styles.label}>Payment history</Text>
                    <Text style={styles.body}>History is not available yet.</Text>
                    <View style={styles.actions}>
                        {isActive && (
                            <>
                                <SolidButton testID={`${testID}-pause`} text="Pause" size="sm" variant="outlined" type="giving" onPress={() => onUpdate?.(gift, 'PAUSED')} />
                                <SolidButton testID={`${testID}-cancel`} text="Cancel" size="sm" variant="outlined" type="error" onPress={() => onUpdate?.(gift, 'CANCELLED')} />
                            </>
                        )}
                        {isPaused && (
                            <>
                                <SolidButton testID={`${testID}-resume`} text="Resume" size="sm" type="giving" onPress={() => onUpdate?.(gift, 'ACTIVE')} />
                                <SolidButton testID={`${testID}-cancel`} text="Cancel" size="sm" variant="outlined" type="error" onPress={() => onUpdate?.(gift, 'CANCELLED')} />
                            </>
                        )}
                        {isCancelled && (
                            <SolidButton text="Cancelled" size="sm" disabled variant="outlined" type="secondary" />
                        )}
                    </View>
                </View>
            )}
        </Pressable>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, shadows, typography }) => ({
    card: {
        padding: spacing.md,
        borderRadius: roundness.md,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        gap: spacing.sm,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    headerLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    amount: {
        fontSize: typography.h2,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    meta: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    statusBadge: {
        paddingHorizontal: spacing.sm,
        paddingVertical: spacing.xs,
        borderRadius: roundness.sm,
        backgroundColor: colors.surfaceVariant,
    },
    activeBadge: {
        backgroundColor: colors.givingContainer,
    },
    pausedBadge: {
        backgroundColor: colors.warningContainer,
    },
    cancelledBadge: {
        backgroundColor: colors.errorContainer,
    },
    statusText: {
        fontSize: typography.micro,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
    },
    activeText: {
        color: colors.giving,
    },
    pausedText: {
        color: colors.warning,
    },
    cancelledText: {
        color: colors.error,
    },
    details: {
        gap: spacing.sm,
        paddingTop: spacing.sm,
        borderTopWidth: 1,
        borderTopColor: colors.borderSubtle,
    },
    label: {
        fontSize: typography.caption,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textTransform: 'uppercase',
    },
    body: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.text,
    },
    actions: {
        flexDirection: 'row',
        gap: spacing.sm,
        paddingTop: spacing.sm,
    },
}));
